import { useState, useEffect } from 'react'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { popModal } from '@/components/modals'
import { useProjectStore } from '@/hooks/use-project-store'
import { UploadCloud, Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Product } from '@/components/product/product-list'
import { uploadBlobToCdn } from '@/lib/utils'
import { nanoid } from 'nanoid'
import { Switch } from '@/components/ui/switch'

interface ProductFormModalProps {
  product?: Product
  onSuccess?: () => void
  onClose?: () => void
  onConnectStripe: () => void
  stripeConnected: boolean
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CNY']
const BILLING_INTERVALS = ['day', 'week', 'month', 'year']
const NANOID_LENGTH = 8

export default function ProductFormModal({
  product,
  onSuccess,
  onClose,
  onConnectStripe,
  stripeConnected,
}: ProductFormModalProps) {
  const t = useTranslations('product')
  const { project } = useProjectStore()
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    photo: '',
    isSubscription: false,
    billingInterval: 'month',
    intervalCount: '1',
  })

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        currency: product.currency,
        photo: product.photo,
        isSubscription: product.is_subscription || false,
        billingInterval: product.billing_interval || 'month',
        intervalCount: product.interval_count?.toString() || '1',
      })
    }
  }, [product])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleCurrencyChange = (value: string) => {
    setForm((prev) => ({ ...prev, currency: value }))
  }

  const handleBillingIntervalChange = (value: string) => {
    setForm((prev) => ({ ...prev, billingInterval: value }))
  }

  const handleSubscriptionToggle = (checked: boolean) => {
    setForm((prev) => ({ ...prev, isSubscription: checked }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    if (!stripeConnected) {
      onConnectStripe()
      setLoading(false)
      return
    }

    try {
      const price = Number.parseFloat(form.price)
      if (Number.isNaN(price) || price <= 0 || price > 100000) {
        toast.error(t('invalid_price'))
        setLoading(false)
        return
      }

      // Validate interval count if subscription
      if (form.isSubscription) {
        const intervalCount = Number.parseInt(form.intervalCount)
        if (Number.isNaN(intervalCount) || intervalCount <= 0) {
          toast.error(t('invalid_interval_count'))
          setLoading(false)
          return
        }
      }

      const method = product ? 'PUT' : 'POST'
      const url = product
        ? `/api/projects/${project?._id}/products/${product._id}`
        : `/api/projects/${project?._id}/products`

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          price: Number.parseFloat(form.price),
          currency: form.currency,
          photo: form.photo,
          isSubscription: form.isSubscription,
          billingInterval: form.isSubscription ? form.billingInterval : null,
          intervalCount: form.isSubscription
            ? Number.parseInt(form.intervalCount)
            : null,
          returnURL: project?.deploy_url,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        toast.success(product ? t('product_updated') : t('product_created'))
        onSuccess?.()
        onClose?.()
        popModal()
      } else {
        if (data.error === 'Stripe account not connected') {
          toast.error(t('stripe_not_connected'))
        } else if (data.error === 'Stripe account setup is not complete') {
          toast.error(t('stripe_setup_incomplete'))
        } else {
          toast.error(data.error || t('submission_error'))
        }
      }
    } catch (error) {
      console.error('Error submitting product:', error)
      toast.error(t('submission_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const extension = file.name.split('.').pop()
      const filename = `user-product/${project?._id}/${nanoid(NANOID_LENGTH)}.${extension}`
      const url = await uploadBlobToCdn(file, filename, file.type)

      setForm((prev) => ({ ...prev, photo: url }))

      toast.success(t('image_upload_success'))
    } catch (error) {
      console.error('Error uploading image:', error)
      toast.error(t('image_upload_error'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          {product ? t('edit_product') : t('add_product')}
        </DialogTitle>
        <DialogDescription>{t('product_form_description')}</DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">{t('product_name')}</Label>
          <Input
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={t('product_name_placeholder')}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">{t('product_description')}</Label>
          <Textarea
            id="description"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder={t('product_description_placeholder')}
            rows={3}
            required
          />
        </div>

        <div className="flex gap-4">
          <div className="space-y-2 flex-1">
            <Label htmlFor="price">{t('product_price')}</Label>
            <Input
              id="price"
              name="price"
              type="number"
              step="0.01"
              min="0.01"
              max="100000"
              value={form.price}
              onChange={handleChange}
              placeholder="9.99"
              required
            />
          </div>

          <div className="space-y-2 w-1/3">
            <Label htmlFor="currency">{t('product_currency')}</Label>
            <Select value={form.currency} onValueChange={handleCurrencyChange}>
              <SelectTrigger id="currency">
                <SelectValue placeholder={t('select_currency')} />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((currency) => (
                  <SelectItem key={currency} value={currency}>
                    {currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Switch
              id="subscription-toggle"
              checked={form.isSubscription}
              onCheckedChange={handleSubscriptionToggle}
            />
            <Label htmlFor="subscription-toggle">{t('is_subscription')}</Label>
          </div>
        </div>

        {form.isSubscription && (
          <div className="space-y-4 p-3 rounded-md">
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <Label htmlFor="billingInterval">{t('billing_interval')}</Label>
                <Select
                  value={form.billingInterval}
                  onValueChange={handleBillingIntervalChange}
                >
                  <SelectTrigger id="billingInterval">
                    <SelectValue placeholder={t('select_interval')} />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_INTERVALS.map((interval) => (
                      <SelectItem key={interval} value={interval}>
                        {t(`interval_${interval}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 w-1/3">
                <Label htmlFor="intervalCount">{t('interval_count')}</Label>
                <Input
                  id="intervalCount"
                  name="intervalCount"
                  type="number"
                  min="1"
                  value={form.intervalCount}
                  onChange={handleChange}
                  placeholder="1"
                  required={form.isSubscription}
                />
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="photo">{t('product_photo')}</Label>
          <div className="flex items-center gap-4">
            {form.photo && (
              <div className="h-20 w-20 rounded-md overflow-hidden border">
                <img
                  src={form.photo}
                  alt={form.name}
                  className="object-cover w-full h-full"
                />
              </div>
            )}
            <div className="flex-1">
              <Label
                htmlFor="photo-upload"
                className="flex items-center justify-center w-full h-10 px-4 border border-gray-300 rounded-md cursor-pointer"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <UploadCloud className="h-4 w-4 mr-2" />
                )}
                {uploading ? t('uploading') : t('upload_image')}
              </Label>
              <Input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline_dark"
            onClick={() => {
              onClose?.()
              popModal()
            }}
            disabled={loading || uploading}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" variant="light" disabled={loading || uploading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {product ? t('update_product') : t('create_product')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
